// Comment Card Styles
// Flat comment styling

import styled, { css } from 'styled-components';
import { theme } from '../theme';
import { ChatRoomTheme } from '../../contexts/ChatRoomThemeContext';

export const CommentCardContainer = styled.div<{ $theme?: ChatRoomTheme }>`
  background: ${props => props.$theme === 'light' 
    ? 'rgba(0, 0, 0, 0.02)' 
    : 'rgba(255, 255, 255, 0.03)'};
  border-left: 2px solid ${props => props.$theme === 'light' 
    ? 'rgba(0, 0, 0, 0.1)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  display: flex;
  gap: 0.75rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.875rem;
    gap: 0.625rem;
    margin-bottom: 0.625rem;
  }
`;

export const CommentVoteSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  min-width: 40px;
  flex-shrink: 0;
`;

export const CommentVoteButton = styled.button<{ $isActive?: boolean; $direction: 'up' | 'down'; $theme?: ChatRoomTheme }>`
  background: none;
  border: none;
  color: ${({ $isActive, $direction, $theme }) => {
    if ($isActive && $direction === 'up') return theme.colors.primary;
    if ($isActive && $direction === 'down') return '#4A90E2';
    return $theme === 'light' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)';
  }};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 1rem;

  &:hover {
    background: ${props => props.$theme === 'light' 
      ? 'rgba(0, 0, 0, 0.05)' 
      : 'rgba(255, 255, 255, 0.1)'};
    color: ${({ $direction }) => ($direction === 'up' ? theme.colors.primary : '#4A90E2')};
  }

  &:active {
    transform: scale(0.9);
  }
`;

export const CommentVoteCount = styled.div<{ $isPositive?: boolean; $theme?: ChatRoomTheme }>`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ $isPositive, $theme }) => {
    if ($isPositive) return theme.colors.primary;
    return $theme === 'light' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)';
  }};
  min-height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CommentContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

export const CommentMeta = styled.div<{ $theme?: ChatRoomTheme }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'};
`;

export const CommentAuthor = styled.span<{ $theme?: ChatRoomTheme; $clickable?: boolean }>`
  font-weight: 600;
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)'};

  ${props => props.$clickable && css`
    cursor: pointer;

    &:hover,
    &:focus-visible {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: 3px;
    }
  `}
`;

export const CommentTime = styled.span``;

export const CommentBody = styled.p<{ $theme?: ChatRoomTheme }>`
  font-size: 0.9rem;
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.85)'};
  margin: 0;
  line-height: 1.5;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.85rem;
  }
`;

export const CommentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

export const ReplyButton = styled.button<{ $theme?: ChatRoomTheme }>`
  background: none;
  border: none;
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'};
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$theme === 'light'
      ? 'rgba(0, 0, 0, 0.05)'
      : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)'};
  }
`;

export const ReplyBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

export const ReplyTextarea = styled.textarea<{ $theme?: ChatRoomTheme }>`
  width: 100%;
  min-height: 60px;
  padding: 0.6rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: inherit;
  resize: vertical;
  border: 1px solid ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.15)'};
  background: ${props => props.$theme === 'light' ? 'white' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)'};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

export const ReplyBoxActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

export const ReplyCancelButton = styled.button<{ $theme?: ChatRoomTheme }>`
  background: none;
  border: none;
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'};
  cursor: pointer;
  padding: 0.4rem 0.75rem;
  font-size: 0.8rem;
`;

export const ReplySubmitButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const RepliesList = styled.div<{ $theme?: ChatRoomTheme }>`
  margin-top: 0.5rem;
  padding-left: 1rem;
  border-left: 2px solid ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

